import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { FileSystemNavigator } from '../../utils/filesystem';
import { TerminalSettingsDialog, TerminalSettings } from './TerminalSettings';
import { useUserSettings } from '../../hooks/useUserSettings';
import { useToast } from '../../hooks/use-toast';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { buildMapleMythosKnowledgeBaseDescription, buildMapleMythosKnowledgeBaseDocuments, buildMapleMythosKnowledgeBaseName } from '@/lib/demythos/knowledgeBase';
import { PROGRAMS } from '../../utils/programs';
import { getActivitySnapshot } from '../../lib/activity';
import { executeMapleClawCli } from '@/lib/mapleclawCli';
import { mapleMythosRuntime as deMythosRuntime } from '@/lib/demythos/runtime';
import type { MapleMythosRunState } from '@/lib/demythos/types';
import MapleCodeREPL from './MapleCodeREPL';
import DeMythos from './DeMythos';
import { findMatchingSshProfile, findSshProfileByReference, normalizeSshProfiles, removeSshProfile, upsertSshProfile } from '../../lib/sshProfiles';
import type { SshActiveSession, SshConnectRequest, SshProfile, SshPrompt, SshRendererEvent } from '../../types/ssh';
import { parseSshCommandArgs, splitSshDestination } from './terminal/sshCommandParser';
import SshTerminalViewport from './terminal/SshTerminalViewport';
import { SshConfigApp } from './terminal/SshConfigApp';

interface TerminalProps {
  navigator?: FileSystemNavigator;
  onFileSystemChange?: () => void;
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  filePath?: string; // optional script path to auto-run
  fileContent?: string;
}

interface CommandHistory {
  command: string;
  prompt: string;
  output: TerminalOutput[];
  timestamp: Date;
}

interface TerminalOutput {
  text: string;
  type: 'normal' | 'error' | 'success' | 'warning' | 'info' | 'path' | 'file' | 'folder';
}

type MapleCodeTerminalBridgeResult =
  | { kind: 'output'; text: string }
  | { kind: 'input'; prompt: string; secret?: boolean };

interface PendingSshPromptState {
  prompt: string;
  description?: string;
  secret?: boolean;
}

interface PendingBridgeSshOperation {
  resolve: (result: MapleCodeTerminalBridgeResult) => void;
  chunks: string[];
  fallbackText?: string;
  idleTimer: number | null;
}

const MAPLECODE_ASSISTANT_BOOT_PATH = '__maplecode__';
const DEMYTHOS_ASSISTANT_BOOT_PATH = '__demythos__';
const MAX_COMMAND_HISTORY = 200;
const BUILTIN_COMMANDS = [
  'help',
  'cls',
  'clear',
  'dir',
  'ls',
  'cd',
  'type',
  'cat',
  'mkdir',
  'rmdir',
  'copy',
  'xcopy',
  'robocopy',
  'del',
  'erase',
  'move',
  'echo',
  'input',
  'pwd',
  'tree',
  'ver',
  'time',
  'top',
  'maplecode',
  'demythos',
  'ssh',
  'ssh-config',
  'ssh-profiles',
  'exit',
  'set',
  'call',
  'attrib',
  'systeminfo',
  'hostname',
  'tasklist',
  'taskkill',
  'msinfo32',
  'driverquery',
  'chkdsk',
  'sfc',
  'ipconfig',
  'ping',
  'tracert',
  'netstat',
  'nslookup',
  'net',
  'netsh',
  'arp',
  'route',
  'telnet',
  'mapleclaw',
];

function normalizeCommandHistory(entries: unknown): string[] {
  if (!Array.isArray(entries)) return [];

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const value = String(entries[index] || '').trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    normalized.unshift(value);
  }

  return normalized.slice(-MAX_COMMAND_HISTORY);
}

function detectLikelyShellPrompt(output: string) {
  const normalized = output.replace(/\r/g, '');
  const lines = normalized.split('\n');
  const lastNonEmptyLine = [...lines].reverse().find((line) => line.trim().length > 0) || '';

  return /^[A-Za-z]:\\.*>\s*$/.test(lastNonEmptyLine)
    || /[^\n\r]*@[^\n\r]+:[^\n\r]*[#$>]\s*$/.test(lastNonEmptyLine)
    || /(?:^|\s)\/?[^\n\r]*[#$>]\s*$/.test(lastNonEmptyLine);
}

function findActiveDeMythosPhase(run: MapleMythosRunState | null) {
  if (!run || run.status !== 'running') {
    return null;
  }

  for (let index = run.transcript.length - 1; index >= 0; index -= 1) {
    const entry = run.transcript[index];
    if (entry.kind !== 'phase' || !entry.phase || entry.message !== `${entry.phase} phase started.`) {
      continue;
    }

    const completed = run.transcript.slice(index + 1).some((candidate) => (
      candidate.phase === entry.phase
      && candidate.loopIndex === entry.loopIndex
      && candidate.message === `${entry.phase} phase completed.`
    ));

    if (!completed) {
      return entry.phase;
    }
  }

  return null;
}

function buildDeMythosTerminalStatus(
  run: MapleMythosRunState | null,
  options: {
    isBridgeBusy: boolean;
    waitingForPrompt: boolean;
  }
) {
  if (!run) {
    return null;
  }

  const activePhase = findActiveDeMythosPhase(run);
  const latestEntry = run.transcript.at(-1) || null;
  const latestMessage = latestEntry?.message || 'No transcript events yet.';

  if (options.waitingForPrompt) {
    return {
      headline: 'DeMythos is waiting for SSH input',
      detail: latestMessage,
    };
  }

  if (options.isBridgeBusy) {
    return {
      headline: 'DeMythos is executing a remote terminal command',
      detail: latestMessage,
    };
  }

  if (activePhase) {
    return {
      headline: `DeMythos is reasoning in the ${activePhase} phase`,
      detail: latestMessage,
    };
  }

  if (run.status === 'paused') {
    return {
      headline: 'DeMythos is paused',
      detail: latestMessage,
    };
  }

  if (run.status === 'running') {
    return {
      headline: 'DeMythos is between terminal work and the next reasoning step',
      detail: latestMessage,
    };
  }

  return {
    headline: `DeMythos run is ${run.status}`,
    detail: latestMessage,
  };
}

export const Terminal: React.FC<TerminalProps> = ({ navigator, onFileSystemChange, onOpenWindow, filePath, fileContent }) => {
  const { toast } = useToast();
  const { createKnowledgeBase, addDocument: addKnowledgeBaseDocument } = useKnowledgeBases();
  
  // Supabase-backed terminal settings
  const { settings: savedSettings, updateSetting, isLoaded } = useUserSettings('terminal', {
    backgroundOpacity: 100,
    fontSize: 14,
    fontFamily: 'monospace',
    textColor: 'green',
    cursorStyle: 'block',
    scrollback: 1000,
    commandHistory: [],
    sshProfiles: [],
  });

  const sshProfiles = useMemo(() => normalizeSshProfiles((savedSettings as { sshProfiles?: SshProfile[] })?.sshProfiles), [savedSettings]);
  const isElectronSshAvailable = Boolean(window.electronAPI?.isElectron && window.electronAPI?.ssh);

  const [currentPath, setCurrentPath] = useState('C:\\');
  const [inputValue, setInputValue] = useState('');
  const [history, setHistory] = useState<CommandHistory[]>([]);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [reverseSearchActive, setReverseSearchActive] = useState(false);
  const [reverseSearchQuery, setReverseSearchQuery] = useState('');
  const [reverseSearchMatchIndex, setReverseSearchMatchIndex] = useState(-1);
  const [pendingTerminalInputPrompt, setPendingTerminalInputPrompt] = useState<string | null>(null);
  const [pendingSshPrompt, setPendingSshPrompt] = useState<PendingSshPromptState | null>(null);
  const [activeSshSession, setActiveSshSession] = useState<SshActiveSession | null>(null);
  const [sshViewportFailed, setSshViewportFailed] = useState(false);
  const [deMythosRuns, setDeMythosRuns] = useState<MapleMythosRunState[]>(() => deMythosRuntime.listRuns());
  const [isAssistantBridgeBusy, setIsAssistantBridgeBusy] = useState(false);
  
  // Local terminal settings that sync with Supabase
  const [terminalSettings, setTerminalSettings] = useState<TerminalSettings>({
    backgroundOpacity: 100,
    fontSize: 14,
    fontFamily: 'monospace',
    textColor: 'green',
    cursorStyle: 'block',
    scrollback: 1000
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const terminalRef = useRef<HTMLDivElement>(null);
  const commandHistoryRef = useRef<string[]>([]);
  const historyIndexRef = useRef(-1);
  const historyDraftRef = useRef('');
  const reverseSearchDraftRef = useRef('');
  const pendingTerminalInputRef = useRef<((value: string) => Promise<void>) | null>(null);
  const pendingMapleCodeInputRef = useRef<((value: string) => Promise<MapleCodeTerminalBridgeResult>) | null>(null);
  const pendingSshPromptRef = useRef<((value: string) => Promise<void>) | null>(null);
  const activeSshSessionRef = useRef<SshActiveSession | null>(null);
  const sshViewportWriterRef = useRef<((chunk: string) => void) | null>(null);
  const pendingSshOutputBufferRef = useRef<string[]>([]);
  const pendingBridgeSshOperationRef = useRef<PendingBridgeSshOperation | null>(null);
  const knowledgeBaseAutoSaveInFlightRef = useRef(new Set<string>());

  const [interactiveApp, setInteractiveApp] = useState<null | 'top' | 'maplecode' | 'demythos' | 'ssh-config'>(null);
  const [topLines, setTopLines] = useState<string[]>([]);
  const topViewRef = useRef<HTMLDivElement>(null);
  const topStartAtRef = useRef<number>(0);
  const topTimerRef = useRef<number | null>(null);
  const [topTick, setTopTick] = useState(0);
  const mapleCodeInitialPrompt = filePath === MAPLECODE_ASSISTANT_BOOT_PATH ? fileContent?.trim() || '' : '';
  const deMythosInitialPrompt = filePath === DEMYTHOS_ASSISTANT_BOOT_PATH ? fileContent?.trim() || '' : '';
  
  // NEW: metrics for real CPU/memory (best-effort in browser)
  const longTaskDurationsRef = useRef<{ t: number; d: number }[]>([]);
  const lastCpuRef = useRef<number | null>(null);
  const lastMemRef = useRef<number | null>(null);
  const lastMemDetailRef = useRef<{ used: number; limit: number } | null>(null);

  // Track auto-run so we don't run twice
  const autoRunRef = useRef(false);

  const getMapleOsEnv = useCallback(() => {
    const hostWindow = window as any;
    hostWindow.__MAPLEOS_ENV = hostWindow.__MAPLEOS_ENV || {};
    return hostWindow.__MAPLEOS_ENV as Record<string, string>;
  }, []);

  useEffect(() => {
    const unsubscribe = deMythosRuntime.subscribe((runs) => {
      setDeMythosRuns(runs);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    for (const run of deMythosRuns) {
      const shouldAutoSave = run.knowledgeBaseAutoSave
        && (run.status === 'completed' || run.status === 'failed' || run.status === 'stopped')
        && run.knowledgeBaseSaveStatus === 'pending';

      if (!shouldAutoSave || knowledgeBaseAutoSaveInFlightRef.current.has(run.id)) {
        continue;
      }

      knowledgeBaseAutoSaveInFlightRef.current.add(run.id);
      deMythosRuntime.updateRunKnowledgeBase(run.id, {
        knowledgeBaseSaveStatus: 'saving',
        knowledgeBaseError: undefined,
      }, `Knowledge base auto-save started for run ${run.id}.`);

      void (async () => {
        const knowledgeBaseName = buildMapleMythosKnowledgeBaseName(run);
        const knowledgeBaseId = await createKnowledgeBase(
          knowledgeBaseName,
          buildMapleMythosKnowledgeBaseDescription(run),
          undefined,
          true,
        );

        if (!knowledgeBaseId) {
          deMythosRuntime.updateRunKnowledgeBase(run.id, {
            knowledgeBaseSaveStatus: 'failed',
            knowledgeBaseName,
            knowledgeBaseError: 'Failed to create the knowledge base container.',
          }, `Knowledge base auto-save failed for run ${run.id}.`);
          return;
        }

        const documents = buildMapleMythosKnowledgeBaseDocuments(run);
        for (const document of documents) {
          const created = await addKnowledgeBaseDocument(knowledgeBaseId, document);
          if (!created) {
            deMythosRuntime.updateRunKnowledgeBase(run.id, {
              knowledgeBaseSaveStatus: 'failed',
              knowledgeBaseId,
              knowledgeBaseName,
              knowledgeBaseError: 'One or more knowledge base documents failed to save.',
            }, `Knowledge base auto-save failed for run ${run.id}.`);
            return;
          }
        }

        deMythosRuntime.updateRunKnowledgeBase(run.id, {
          knowledgeBaseSaveStatus: 'saved',
          knowledgeBaseId,
          knowledgeBaseName,
          knowledgeBaseError: undefined,
          knowledgeBaseSavedAt: new Date().toISOString(),
        }, `Knowledge base auto-saved as "${knowledgeBaseName}".`);
      })().finally(() => {
        knowledgeBaseAutoSaveInFlightRef.current.delete(run.id);
      });
    }
  }, [addKnowledgeBaseDocument, createKnowledgeBase, deMythosRuns]);

  useEffect(() => {
    activeSshSessionRef.current = activeSshSession;
  }, [activeSshSession]);

  const expandEnvironmentVariables = useCallback(
    (value: string) => value.replace(/%([^%]+)%/g, (_match, rawKey) => getMapleOsEnv()[String(rawKey).trim()] || ''),
    [getMapleOsEnv],
  );

  const parseSetPromptCommand = useCallback((value: string) => {
    const match = value.trim().match(/^set\s+\/p\s+([A-Za-z_][A-Za-z0-9_]*)=(.*)$/i);
    if (!match) {
      return null;
    }

    return {
      variableName: match[1],
      prompt: (match[2] || '').trim() || `${match[1]}:`,
    };
  }, []);

  const resolveTerminalPath = useCallback(
    (pathInput: string) => (
      /^[a-z]:\\/i.test(pathInput)
        ? pathInput.replace(/^c:\\/i, 'C:\\')
        : currentPath === 'C:\\'
          ? `C:\\${pathInput}`
          : `${currentPath}\\${pathInput}`
    ),
    [currentPath],
  );

  const readScriptFile = useCallback(async (pathInput: string) => {
    if (!navigator) {
      return { error: 'File system not available.' } as const;
    }

    await navigator.waitForInitialization();
    if (navigator.syncWithVFS) {
      await navigator.syncWithVFS();
    }

    const resolvedPath = resolveTerminalPath(pathInput);
    let file = navigator.findItemByPath(resolvedPath);
    if (!file || file.type !== 'file') {
      if (navigator.syncWithVFS) {
        await navigator.syncWithVFS();
      }
      file = navigator.findItemByPath(resolvedPath);
    }

    if (!file || file.type !== 'file') {
      return { error: `The system cannot find the file specified: "${resolvedPath}"` } as const;
    }

    return {
      resolvedPath,
      lines: (file.content || '').split(/\r?\n/),
    } as const;
  }, [navigator, resolveTerminalPath]);

  // Run a script file (.bat or .sh) using terminal commands
  const runScriptFromPath = async (pathInput: string, kind: 'bat' | 'sh' = 'bat') => {
    const script = await readScriptFile(pathInput);
    if ('error' in script) {
      addOutput(pathInput, [{ text: script.error, type: 'error' }]);
      return;
    }

    const { resolvedPath, lines } = script;
    addOutput(pathInput, [{ text: `Executing ${kind.toUpperCase()} script: ${resolvedPath}`, type: 'info' }]);

    const runFromLine = async (startIndex: number) => {
      for (let index = startIndex; index < lines.length; index += 1) {
        const raw = lines[index]?.trim() || '';
        if (!raw) continue;
        if (/^(rem|::)/i.test(raw)) continue;
        if (/^@?echo\s+off$/i.test(raw)) continue;
        if (kind === 'sh' && /^#\!/.test(raw)) continue;

        const expandedLine = expandEnvironmentVariables(raw);
        const setPrompt = parseSetPromptCommand(expandedLine);
        if (setPrompt) {
          setPendingTerminalInputPrompt(setPrompt.prompt);
          pendingTerminalInputRef.current = async (value: string) => {
            const env = getMapleOsEnv();
            env[setPrompt.variableName] = value;
            setPendingTerminalInputPrompt(null);
            pendingTerminalInputRef.current = null;
            await runFromLine(index + 1);
          };
          return;
        }

        await executeCommand(expandedLine);
      }

      addOutput(pathInput, [{ text: `Script finished: ${resolvedPath}`, type: 'success' }]);
    };

    await runFromLine(0);
  };

  const runScriptFromPathForMapleCode = async (pathInput: string, kind: 'bat' | 'sh' = 'bat'): Promise<MapleCodeTerminalBridgeResult> => {
    const script = await readScriptFile(pathInput);
    if ('error' in script) {
      return { kind: 'output', text: script.error };
    }

    const { resolvedPath, lines } = script;

    const runFromLine = async (startIndex: number): Promise<MapleCodeTerminalBridgeResult> => {
      const collected: string[] = [];

      for (let index = startIndex; index < lines.length; index += 1) {
        const raw = lines[index]?.trim() || '';
        if (!raw) continue;
        if (/^(rem|::)/i.test(raw)) continue;
        if (/^@?echo\s+off$/i.test(raw)) continue;
        if (kind === 'sh' && /^#\!/.test(raw)) continue;

        const expandedLine = expandEnvironmentVariables(raw);
        const setPrompt = parseSetPromptCommand(expandedLine);
        if (setPrompt) {
          pendingMapleCodeInputRef.current = async (value: string) => {
            const env = getMapleOsEnv();
            env[setPrompt.variableName] = value;
            return runFromLine(index + 1);
          };
          return { kind: 'input', prompt: setPrompt.prompt };
        }

        const output = await executeCommand(expandedLine);
        if (output.length) {
          collected.push(...output.map((line) => line.text));
        }
      }

      collected.push(`Script finished: ${resolvedPath}`);
      return {
        kind: 'output',
        text: collected.filter(Boolean).join('\n') || `Script finished: ${resolvedPath}`,
      };
    };

    return runFromLine(0);
  };

  // Focus input on mount and when requested via custom event
  useEffect(() => {
    const focusNow = () => inputRef.current?.focus();
    const timer = window.setTimeout(focusNow, 0);

    const handleFocusEvent = () => focusNow();
    document.addEventListener('focusTerminalInput', handleFocusEvent as EventListener);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('focusTerminalInput', handleFocusEvent as EventListener);
    };
  }, []);

  // Focus input on mount and click
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  // Scroll to bottom when new output is added (skip when interactive app is active)
  useEffect(() => {
    if (interactiveApp) return;
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history, interactiveApp]);

  // Handle opening settings via custom event
  useEffect(() => {
    const handleOpenSettings = (e: CustomEvent) => {
      setShowSettings(true);
    };

    document.addEventListener('openTerminalSettings', handleOpenSettings as EventListener);
    return () => {
      document.removeEventListener('openTerminalSettings', handleOpenSettings as EventListener);
    };
  }, []);

  // Sync settings from Supabase to local state when first loaded (only once)
  useEffect(() => {
    if (isLoaded && savedSettings) {
      console.log('Terminal: Loading saved settings:', savedSettings);
      setTerminalSettings({
        backgroundOpacity: savedSettings.backgroundOpacity || 100,
        fontSize: savedSettings.fontSize || 14,
        fontFamily: savedSettings.fontFamily || 'monospace',
        textColor: savedSettings.textColor || 'green',
        cursorStyle: savedSettings.cursorStyle || 'block',
        scrollback: savedSettings.scrollback || 1000
      });

      const persistedHistory = normalizeCommandHistory(savedSettings.commandHistory);
      commandHistoryRef.current = persistedHistory;
      setCommandHistory(persistedHistory);
    }
  }, [isLoaded, savedSettings]);

  // Custom settings change handler that saves to Supabase
  const handleSettingsChange = (newSettings: TerminalSettings) => {
    console.log('Terminal: Updating settings:', newSettings);
    setTerminalSettings(newSettings);
    
    // Save each setting to Supabase individually
    Object.entries(newSettings).forEach(([key, value]) => {
      updateSetting(key as keyof TerminalSettings | 'commandHistory', value as never);
    });
    
    toast({
      title: "Terminal Settings Saved",
      description: "Your terminal preferences have been saved to your account.",
    });
  };

  const handleSaveSshProfile = useCallback((profile: SshProfile) => {
    const nextProfiles = upsertSshProfile(sshProfiles, profile);
    updateSetting('sshProfiles' as never, nextProfiles as never);
    toast({
      title: 'SSH profile saved',
      description: `${profile.name} is ready to use with the ssh command.`,
    });
  }, [sshProfiles, toast, updateSetting]);

  const handleDeleteSshProfile = useCallback((profileId: string) => {
    const nextProfiles = removeSshProfile(sshProfiles, profileId);
    updateSetting('sshProfiles' as never, nextProfiles as never);
    toast({
      title: 'SSH profile deleted',
      description: 'The saved SSH host profile has been removed.',
    });
  }, [sshProfiles, toast, updateSetting]);

  // When 'top' is active, start observing long tasks for main-thread busy%
  useEffect(() => {
    if (interactiveApp !== 'top') return;

    let observer: PerformanceObserver | null = null;

    // Long Tasks API support check
    const supported = (PerformanceObserver as any)?.supportedEntryTypes?.includes?.('longtask');
    if (supported) {
      observer = new PerformanceObserver((list) => {
        const now = performance.now();
        for (const e of list.getEntries()) {
          longTaskDurationsRef.current.push({ t: now, d: e.duration });
        }
        // keep a rolling ~60s window
        longTaskDurationsRef.current = longTaskDurationsRef.current.filter(x => now - x.t <= 60_000);
      });
      try {
        observer.observe({ entryTypes: ['longtask'] as any });
      } catch {
        // ignore if browser lies about support
      }
    }

    return () => {
      observer?.disconnect();
    };
  }, [interactiveApp]);

  // Render/update the 'top' display while active
  useEffect(() => {
    if (interactiveApp !== 'top') return;

    const KB = 1024;
    const MB = 1024 * KB;

    const renderTop = () => {
      const now = new Date();
      const uptimeMs = Date.now() - topStartAtRef.current;
      const uptimeSec = Math.floor(uptimeMs / 1000);
      const hh = String(Math.floor(uptimeSec / 3600)).padStart(2, '0');
      const mm = String(Math.floor((uptimeSec % 3600) / 60)).padStart(2, '0');
      const ss = String(uptimeSec % 60).padStart(2, '0');

      // CPU estimate = long task busy% over rolling 10s window
      const nowPerf = performance.now();
      longTaskDurationsRef.current = longTaskDurationsRef.current.filter(x => nowPerf - x.t <= 10_000);
      const busyMs = longTaskDurationsRef.current.reduce((sum, x) => sum + x.d, 0);
      const cpuPct = Math.max(0, Math.min(100, (busyMs / 10_000) * 100));
      lastCpuRef.current = cpuPct;

      // Memory estimate from Performance API when available
      let usedMB = 0;
      let limitMB = 0;
      const perfAny: any = performance as any;
      if (perfAny?.memory) {
        usedMB = Math.round((perfAny.memory.usedJSHeapSize || 0) / MB);
        limitMB = Math.round((perfAny.memory.jsHeapSizeLimit || 0) / MB);
        lastMemRef.current = limitMB > 0 ? Math.max(0, Math.min(100, (usedMB / limitMB) * 100)) : null;
        lastMemDetailRef.current = { used: usedMB, limit: limitMB };
      } else {
        lastMemRef.current = null;
        lastMemDetailRef.current = null;
      }

      const snapshot = getActivitySnapshot();
      const activity = [...Object.values(snapshot)]
        .sort((a, b) => b.recentMs - a.recentMs)
        .slice(0, 12);

      const lines: string[] = [];
      lines.push('MapleOS top — live process/activity view');
      lines.push(`Updated: ${now.toLocaleTimeString()}    Uptime: ${hh}:${mm}:${ss}`);
      lines.push(`CPU: ${cpuPct.toFixed(1).padStart(5)}%    MEM: ${lastMemDetailRef.current ? `${usedMB}/${limitMB} MB (${(lastMemRef.current || 0).toFixed(1)}%)` : 'n/a (browser-limited)'}`);
      lines.push('Controls: Ctrl+C or q to exit • CPU = simulated • MEM = JS heap • ACT = per-window recent activity');
      lines.push('');
      lines.push('PID   APP                         ACT   LAST');
      lines.push('----  --------------------------  ----  ----------------');

      activity.forEach((item, i) => {
        const pid = String(i + 1000).padEnd(4, ' ');
        const app = item.appId.slice(0, 26).padEnd(26, ' ');
        const act = String(Math.round(item.pct)).padStart(4, ' ');
        const last = `${Math.round(item.recentMs)}ms`;
        lines.push(`${pid}  ${app}  ${act}  ${last}`);
      });

      if (activity.length === 0) {
        lines.push('n/a   No recent app activity recorded yet.');
      }

      setTopLines(lines);
      setTopTick(t => t + 1);
    };

    topStartAtRef.current = Date.now();
    renderTop();
    topTimerRef.current = window.setInterval(renderTop, 1000);

    return () => {
      if (topTimerRef.current) {
        window.clearInterval(topTimerRef.current);
        topTimerRef.current = null;
      }
    };
  }, [interactiveApp]);

  const startTop = () => {
    addOutput('top', [{ text: 'Entering MapleOS top (Ctrl+C or q to exit)...', type: 'info' }]);
    setInteractiveApp('top');
    // focus the top pane so users can immediately quit with q / ctrl+c
    window.setTimeout(() => topViewRef.current?.focus(), 0);
  };

  const stopTop = () => {
    setInteractiveApp(null);
    addOutput('', [{ text: 'Exited top.', type: 'info' }]);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const exitInteractiveApp = () => {
    setInteractiveApp(null);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Capture Ctrl+C / q while top is active
  useEffect(() => {
    if (interactiveApp !== 'top') return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey && (e.key === 'c' || e.key === 'C')) || e.key === 'q' || e.key === 'Q') {
        e.preventDefault();
        stopTop();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [interactiveApp]);

  const formatFileSize = (size: string | undefined): string => {
    return size || '0 bytes';
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString();
  };

  const addOutput = (command: string, output: TerminalOutput[]) => {
    const session = activeSshSessionRef.current;
    const prompt = session
      ? `ssh:${session.username}@${session.host}${session.status === 'connecting' ? ' (connecting)' : ''}>`
      : `${currentPath}>`;

    setHistory(prev => [...prev, {
      command,
      prompt,
      output,
      timestamp: new Date()
    }]);
  };

  const queueTerminalInput = useCallback((prompt: string, onSubmit: (value: string) => Promise<void>) => {
    setPendingTerminalInputPrompt(prompt);
    pendingTerminalInputRef.current = async (value: string) => {
      setPendingTerminalInputPrompt(null);
      pendingTerminalInputRef.current = null;
      await onSubmit(value);
    };
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const queueSshPrompt = useCallback((prompt: PendingSshPromptState, onSubmit: (value: string) => Promise<void>) => {
    setIsProcessing(false);
    setPendingSshPrompt(prompt);
    pendingSshPromptRef.current = async (value: string) => {
      setPendingSshPrompt(null);
      pendingSshPromptRef.current = null;
      await onSubmit(value);
    };

    const pendingBridgeOperation = pendingBridgeSshOperationRef.current;
    if (pendingBridgeOperation) {
      if (pendingBridgeOperation.idleTimer) {
        window.clearTimeout(pendingBridgeOperation.idleTimer);
      }
      setIsAssistantBridgeBusy(false);
      pendingBridgeSshOperationRef.current = null;
      pendingBridgeOperation.resolve({ kind: 'input', prompt: prompt.prompt, secret: prompt.secret });
    }

    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const resolvePendingBridgeSshOperation = useCallback((result: MapleCodeTerminalBridgeResult) => {
    const pendingOperation = pendingBridgeSshOperationRef.current;
    if (!pendingOperation) {
      return;
    }

    if (pendingOperation.idleTimer) {
      window.clearTimeout(pendingOperation.idleTimer);
    }

    setIsAssistantBridgeBusy(false);
    pendingBridgeSshOperationRef.current = null;
    pendingOperation.resolve(result);
  }, []);

  const schedulePendingBridgeSshOutputFlush = useCallback(() => {
    const pendingOperation = pendingBridgeSshOperationRef.current;
    if (!pendingOperation) {
      return;
    }

    if (pendingOperation.idleTimer) {
      window.clearTimeout(pendingOperation.idleTimer);
    }

    pendingOperation.idleTimer = window.setTimeout(() => {
      const latestOperation = pendingBridgeSshOperationRef.current;
      if (!latestOperation) {
        return;
      }

      const text = latestOperation.chunks.join('').replace(/\r/g, '').trim() || latestOperation.fallbackText || 'Command executed.';
      resolvePendingBridgeSshOperation({ kind: 'output', text });
    }, 350);
  }, [resolvePendingBridgeSshOperation]);

  const handleSshViewportWriterChange = useCallback((writer: ((chunk: string) => void) | null) => {
    sshViewportWriterRef.current = writer;
    if (!writer || pendingSshOutputBufferRef.current.length === 0) {
      return;
    }

    for (const chunk of pendingSshOutputBufferRef.current) {
      writer(chunk);
    }
    pendingSshOutputBufferRef.current = [];
  }, []);

  const handleSshViewportError = useCallback((error: unknown) => {
    console.error('SSH terminal viewport failed:', error);
    sshViewportWriterRef.current = null;
    setSshViewportFailed(true);
    toast({
      title: 'SSH terminal fallback enabled',
      description: 'The interactive SSH screen renderer failed, so MapleOS switched back to plain terminal output for this session.',
      variant: 'destructive',
    });
  }, [toast]);

  const sendInputToActiveSshSession = useCallback((input: string) => {
    const session = activeSshSessionRef.current;
    if (!session || session.status !== 'connected' || !window.electronAPI?.ssh) {
      return;
    }

    void window.electronAPI.ssh.sendInput({
      sessionId: session.sessionId,
      input,
    });
  }, []);

  const resizeActiveSshSession = useCallback((cols: number, rows: number) => {
    const session = activeSshSessionRef.current;
    if (!session || session.status !== 'connected' || !window.electronAPI?.ssh?.resize) {
      return;
    }

    void window.electronAPI.ssh.resize({
      sessionId: session.sessionId,
      cols,
      rows,
    });
  }, []);

  const interruptActiveSshSession = useCallback(() => {
    const session = activeSshSessionRef.current;
    if (!session || !window.electronAPI?.ssh) {
      return;
    }

    if (session.status === 'connected') {
      void window.electronAPI.ssh.sendInput({
        sessionId: session.sessionId,
        input: '\u0003',
      });
      return;
    }

    setActiveSshSession({
      ...session,
      status: 'disconnecting',
    });

    void window.electronAPI.ssh.disconnect({
      sessionId: session.sessionId,
      reason: 'interrupt',
    });
  }, []);

  const setHistoryCursor = (nextIndex: number) => {
    historyIndexRef.current = nextIndex;
    setHistoryIndex(nextIndex);
  };

  const clearReverseSearch = (restoreDraft = false) => {
    const draft = reverseSearchDraftRef.current;
    setReverseSearchActive(false);
    setReverseSearchQuery('');
    setReverseSearchMatchIndex(-1);
    if (restoreDraft) {
      setInputValue(draft);
    }
  };

  const setCommandHistoryState = (nextHistory: string[], persist = true) => {
    const normalizedHistory = normalizeCommandHistory(nextHistory);
    commandHistoryRef.current = normalizedHistory;
    setCommandHistory(normalizedHistory);
    if (persist) {
      updateSetting('commandHistory', normalizedHistory as never);
    }
  };

  const findHistoryMatch = (query: string, startIndex?: number) => {
    const historyItems = commandHistoryRef.current;
    if (historyItems.length === 0) return -1;

    const normalizedQuery = query.trim().toLowerCase();
    let index = typeof startIndex === 'number' ? startIndex : historyItems.length - 1;

    for (; index >= 0; index -= 1) {
      const candidate = historyItems[index] || '';
      if (!normalizedQuery || candidate.toLowerCase().includes(normalizedQuery)) {
        return index;
      }
    }

    return -1;
  };

  const updateReverseSearch = (query: string, startIndex?: number) => {
    const matchIndex = findHistoryMatch(query, startIndex);
    setReverseSearchActive(true);
    setReverseSearchQuery(query);
    setReverseSearchMatchIndex(matchIndex);
    setInputValue(matchIndex >= 0 ? commandHistoryRef.current[matchIndex] || '' : reverseSearchDraftRef.current);
  };

  const tokenizeCommandLine = (input: string) => {
    const tokens: string[] = [];
    let current = '';
    let quote: '"' | '\'' | null = null;

    for (let index = 0; index < input.length; index += 1) {
      const char = input[index];
      const next = input[index + 1];

      if (quote) {
        if (char === '\\' && next === quote) {
          current += quote;
          index += 1;
          continue;
        }

        if (char === quote) {
          quote = null;
          continue;
        }

        current += char;
        continue;
      }

      if (char === '"' || char === '\'') {
        quote = char;
        continue;
      }

      if (/\s/.test(char)) {
        if (current) {
          tokens.push(current);
          current = '';
        }
        continue;
      }

      current += char;
    }

    if (current) {
      tokens.push(current);
    }

    return tokens;
  };

  const disconnectActiveSshSession = useCallback(async (reason: 'user' | 'remote' | 'error' | 'interrupt' = 'user') => {

    const session = activeSshSessionRef.current;
    if (!session || !window.electronAPI?.ssh) {
      return;
    }

    setActiveSshSession({
      ...session,
      status: 'disconnecting',
    });

    await window.electronAPI.ssh.disconnect({
      sessionId: session.sessionId,
      reason,
    });
  }, []);

  const handleSshPromptEvent = useCallback((sessionId: string, prompt: SshPrompt) => {
    if (!window.electronAPI?.ssh) {
      return;
    }

    if (prompt.kind === 'host-verification') {
      const description = prompt.changed
        ? `Host fingerprint changed from ${prompt.previousFingerprint || 'unknown'} to ${prompt.fingerprint}`
        : `First-time fingerprint: ${prompt.fingerprint}`;

      queueSshPrompt(
        {
          prompt: `Trust ${prompt.host}:${prompt.port}? Type yes or no: `,
          description,
          secret: false,
        },
        async (value) => {
          const accepted = ['y', 'yes'].includes(value.trim().toLowerCase());
          await window.electronAPI?.ssh?.respondToPrompt({
            sessionId,
            promptId: prompt.promptId,
            accepted,
          });
        },
      );
      return;
    }

    const promptFields = prompt.prompts.length ? prompt.prompts : [{ prompt: 'Response: ', echo: true }];

    const askForPrompt = (index: number, responses: string[]) => {
      const currentPrompt = promptFields[index];
      const description = [prompt.name, prompt.instructions].filter(Boolean).join(' — ');

      queueSshPrompt(
        {
          prompt: currentPrompt.prompt || 'Response: ',
          description: index === 0 ? description : undefined,
          secret: !currentPrompt.echo,
        },
        async (value) => {
          const nextResponses = [...responses, value];
          if (index < promptFields.length - 1) {
            askForPrompt(index + 1, nextResponses);
            return;
          }

          await window.electronAPI?.ssh?.respondToPrompt({
            sessionId,
            promptId: prompt.promptId,
            responses: nextResponses,
          });
        },
      );
    };

    askForPrompt(0, []);
  }, [queueSshPrompt]);

  useEffect(() => {
    if (!window.electronAPI?.ssh?.onEvent) {
      return undefined;
    }

    return window.electronAPI.ssh.onEvent((event: SshRendererEvent) => {
      const currentSession = activeSshSessionRef.current;
      const currentSessionId = currentSession?.sessionId;

      if (!currentSessionId) {
        return;
      }

      const eventSessionId = 'sessionId' in event ? event.sessionId : event.session.sessionId;
      if (eventSessionId !== currentSessionId) {
        return;
      }

      if (event.type === 'ssh-output') {
        const chunk = event.chunk;
        if (!chunk) {
          return;
        }

        const pendingBridgeOperation = pendingBridgeSshOperationRef.current;
        if (pendingBridgeOperation) {
          pendingBridgeOperation.chunks.push(chunk);
          const combinedOutput = pendingBridgeOperation.chunks.join('').replace(/\r/g, '');
          if (detectLikelyShellPrompt(combinedOutput) && !pendingSshPromptRef.current) {
            resolvePendingBridgeSshOperation({ kind: 'output', text: combinedOutput.trim() || pendingBridgeOperation.fallbackText || 'Command executed.' });
          } else {
            schedulePendingBridgeSshOutputFlush();
          }
        }

        if (currentSession?.status === 'connected' && !sshViewportFailed) {
          if (sshViewportWriterRef.current) {
            sshViewportWriterRef.current(chunk);
          } else {
            pendingSshOutputBufferRef.current.push(chunk);
          }
          return;
        }

        addOutput('', [{ text: chunk.replace(/\r/g, ''), type: event.stream === 'stderr' ? 'error' : 'normal' }]);
        return;
      }

      if (event.type === 'ssh-status') {
        activeSshSessionRef.current = event.session;
        setActiveSshSession(event.session);
        if (event.message) {
          addOutput('', [{ text: event.message, type: 'info' }]);
        }

        if (event.session.status === 'connected' || event.session.status === 'error') {
          schedulePendingBridgeSshOutputFlush();
        }
        return;
      }

      if (event.type === 'ssh-prompt') {
        handleSshPromptEvent(event.sessionId, event.prompt);
        return;
      }

      if (event.type === 'ssh-error') {
        const pendingBridgeOperation = pendingBridgeSshOperationRef.current;
        if (pendingBridgeOperation) {
          pendingBridgeOperation.chunks.push(event.error);
          schedulePendingBridgeSshOutputFlush();
        }

        addOutput('', [{ text: event.error, type: 'error' }]);
        return;
      }

      pendingSshOutputBufferRef.current = [];
      setPendingSshPrompt(null);
      pendingSshPromptRef.current = null;
      activeSshSessionRef.current = null;
      setActiveSshSession(null);

      const pendingBridgeOperation = pendingBridgeSshOperationRef.current;
      if (pendingBridgeOperation) {
        pendingBridgeOperation.chunks.push(event.message);
        schedulePendingBridgeSshOutputFlush();
      }

      addOutput('', [{ text: event.message, type: event.reason === 'error' ? 'error' : 'info' }]);
    });
  }, [handleSshPromptEvent, resolvePendingBridgeSshOperation, schedulePendingBridgeSshOutputFlush, sshViewportFailed]);

  const createSshSessionId = () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  const startSshConnection = useCallback(async (request: Omit<SshConnectRequest, 'sessionId'>) => {
    if (!window.electronAPI?.ssh) {
      addOutput('', [{ text: 'SSH is only available in the MapleOS desktop app.', type: 'warning' }]);
      return;
    }

    const sessionId = createSshSessionId();
    const connectingSession: SshActiveSession = {
      sessionId,
      host: request.host,
      port: request.port,
      username: request.username,
      profileId: request.profileId,
      connectedAt: new Date().toISOString(),
      status: 'connecting',
    };

    pendingSshOutputBufferRef.current = [];
    setSshViewportFailed(false);
    activeSshSessionRef.current = connectingSession;
    setActiveSshSession(connectingSession);
    addOutput('', [{ text: `Connecting to ${request.username}@${request.host}:${request.port}...`, type: 'info' }]);

    const result = await window.electronAPI.ssh.connect({
      ...request,
      sessionId,
      cols: 120,
      rows: 36,
    });

    if (!result.ok) {
      activeSshSessionRef.current = null;
      setActiveSshSession(null);
      addOutput('', [{ text: result.error || 'SSH connection failed.', type: 'error' }]);
      return;
    }

    if (result.session) {
      activeSshSessionRef.current = result.session;
      setActiveSshSession(result.session);
    }
  }, []);

  const beginSshConnectionFlow = useCallback(async (
    commandInput: string,
    draft: {
      host: string;
      port: number;
      username: string | null;
      authType: 'password' | 'privateKey';
      privateKey?: string;
      profileId?: string;
    },
  ) => {
    const requireUsername = async (nextDraft: typeof draft) => {
      queueSshPrompt(
        {
          prompt: `SSH username for ${nextDraft.host}: `,
          secret: false,
        },
        async (value) => {
          const username = value.trim();
          if (!username) {
            addOutput(commandInput, [{ text: 'SSH username is required.', type: 'error' }]);
            return;
          }

          await beginSshConnectionFlow(commandInput, {
            ...nextDraft,
            username,
          });
        },
      );
    };

    if (!draft.host) {
      addOutput(commandInput, [{ text: 'SSH host is required.', type: 'error' }]);
      return;
    }

    if (!draft.username) {
      await requireUsername(draft);
      return;
    }

    if (draft.authType === 'privateKey') {
      if (!draft.privateKey?.trim()) {
        addOutput(commandInput, [{ text: 'This SSH profile does not contain a private key.', type: 'error' }]);
        return;
      }

      await startSshConnection({
        host: draft.host,
        port: draft.port,
        username: draft.username,
        authType: 'privateKey',
        privateKey: draft.privateKey,
        profileId: draft.profileId,
      });
      return;
    }

    queueSshPrompt(
      {
        prompt: `${draft.username}@${draft.host}'s password: `,
        secret: true,
      },
      async (password) => {
        await startSshConnection({
          host: draft.host,
          port: draft.port,
          username: draft.username!,
          authType: 'password',
          password,
          profileId: draft.profileId,
        });
      },
    );
  }, [queueSshPrompt, startSshConnection]);

  const parseCommand = (input: string): { command: string; args: string[] } => {
    const parts = tokenizeCommandLine(input.trim());
    const rawCommand = parts[0]?.toLowerCase() || '';
    return {
      command: rawCommand.startsWith('$') ? rawCommand.slice(1) : rawCommand,
      args: parts.slice(1)
    };
  };

  // Helper: resolve a MapleOS program from a command token
  const normalizeLabelToId = (label: string) => label.toLowerCase().replace(/\s+/g, '-');
  const resolveProgram = (cmd: string) => {
    const token = cmd.toLowerCase();
    return PROGRAMS.find(
      (p) => p.id.toLowerCase() === token || normalizeLabelToId(p.label) === token
    );
  };

  const findSharedPrefix = (values: string[]) => {
    if (!values.length) return '';
    let prefix = values[0];

    for (let index = 1; index < values.length; index += 1) {
      while (prefix && !values[index].startsWith(prefix)) {
        prefix = prefix.slice(0, -1);
      }
      if (!prefix) break;
    }

    return prefix;
  };

  const wrapCompletionToken = (value: string) => (/\s/.test(value) ? `"${value}"` : value);

  const buildCommandCompletionCandidates = (needle: string) => {
    const normalizedNeedle = needle.toLowerCase();
    const candidates = Array.from(new Set([
      ...BUILTIN_COMMANDS,
      ...PROGRAMS.map((program) => program.id.toLowerCase()),
      ...PROGRAMS.map((program) => normalizeLabelToId(program.label)),
    ]));

    return candidates.filter((candidate) => candidate.startsWith(normalizedNeedle)).sort();
  };

  const normalizeLookupDirectory = (value: string) => {
    const normalized = value.replace(/^c:\\/i, 'C:\\').replace(/\\+$/, '');
    return /^[A-Z]:$/i.test(normalized) ? `${normalized}\\` : normalized;
  };

  const joinTerminalPath = (base: string, fragment: string) => {
    const trimmedFragment = fragment.replace(/^\\+/, '').replace(/\\+$/, '');
    if (!trimmedFragment) return normalizeLookupDirectory(base);
    return normalizeLookupDirectory(base === 'C:\\' ? `C:\\${trimmedFragment}` : `${base}\\${trimmedFragment}`);
  };

  const buildPathCompletionCandidates = (rawToken: string) => {
    if (!navigator) return [] as { value: string; type: TerminalOutput['type'] }[];

    const token = rawToken.replace(/^['"]|['"]$/g, '');
    const lastSlashIndex = token.lastIndexOf('\\');
    const hasDirectoryPrefix = lastSlashIndex >= 0;
    const typedDirectory = hasDirectoryPrefix ? token.slice(0, lastSlashIndex + 1) : '';
    const fragment = hasDirectoryPrefix ? token.slice(lastSlashIndex + 1) : token;

    let lookupDirectory = currentPath;
    if (typedDirectory) {
      lookupDirectory = /^[a-z]:\\/i.test(typedDirectory)
        ? normalizeLookupDirectory(typedDirectory)
        : joinTerminalPath(currentPath, typedDirectory);
    }

    try {
      return navigator
        .getItemsInPath(lookupDirectory)
        .filter((item) => item.name.toLowerCase().startsWith(fragment.toLowerCase()))
        .map((item) => ({
          value: wrapCompletionToken(`${typedDirectory}${item.name}${item.type === 'folder' ? '\\' : ''}`),
          type: item.type === 'folder' ? 'folder' as const : 'file' as const,
        }))
        .sort((a, b) => a.value.localeCompare(b.value));
    } catch {
      return [];
    }
  };

  const handleTabCompletion = () => {
    const trailingSpace = /\s$/.test(inputValue);
    const tokens = tokenizeCommandLine(inputValue);
    const tokenCount = trailingSpace ? tokens.length + 1 : tokens.length;
    const rawTokenMatch = trailingSpace ? null : inputValue.match(/(?:^|\s)([^\s]*)$/);
    const rawToken = trailingSpace ? '' : rawTokenMatch?.[1] || '';
    const tokenPrefix = rawToken.startsWith('$') ? '$' : '';
    const bareToken = rawToken.replace(/^\$/, '');

    if (tokenCount <= 1) {
      const candidates = buildCommandCompletionCandidates(bareToken);
      if (!candidates.length) return;

      const completedToken = tokenPrefix + (candidates.length === 1 ? candidates[0] : findSharedPrefix(candidates));
      const nextValue = `${inputValue.slice(0, inputValue.length - rawToken.length)}${completedToken}${candidates.length === 1 ? ' ' : ''}`;
      setInputValue(nextValue);

      if (candidates.length > 1) {
        addOutput('', [{ text: candidates.join('    '), type: 'info' }]);
      }
      return;
    }

    const candidates = buildPathCompletionCandidates(rawToken);
    if (!candidates.length) return;

    const replacement = candidates.length === 1
      ? candidates[0].value
      : wrapCompletionToken(findSharedPrefix(candidates.map((candidate) => candidate.value.replace(/^['"]|['"]$/g, ''))));

    const nextValue = `${inputValue.slice(0, inputValue.length - rawToken.length)}${replacement}${candidates.length === 1 && !replacement.endsWith('\\') ? ' ' : ''}`;
    setInputValue(nextValue);

    if (candidates.length > 1) {
      addOutput('', candidates.map((candidate) => ({ text: candidate.value, type: candidate.type })));
    }
  };

  const executeCommand = async (input: string): Promise<TerminalOutput[]> => {
    if (!input.trim()) return [];

    setIsProcessing(true);
    const trimmedInput = expandEnvironmentVariables(input.trim());

    if (/^@?echo\.$/i.test(trimmedInput)) {
      setIsProcessing(false);
      return [{ text: '', type: 'normal' }];
    }

    const echoMatch = trimmedInput.match(/^@?echo(?:\s+(.*))?$/i);
    if (echoMatch) {
      setIsProcessing(false);
      return [{ text: echoMatch[1] ?? '', type: 'normal' }];
    }

    const directSetPrompt = parseSetPromptCommand(trimmedInput);
    if (directSetPrompt) {
      queueTerminalInput(directSetPrompt.prompt, async (value: string) => {
        const env = getMapleOsEnv();
        env[directSetPrompt.variableName] = value;
      });
      setIsProcessing(false);
      return [{ text: `Input requested for %${directSetPrompt.variableName}%`, type: 'info' }];
    }

    const { command, args } = parseCommand(trimmedInput);
    let output: TerminalOutput[] = [];

    try {
      switch (command) {
        case 'set': {
          const env = getMapleOsEnv();
          if (args.length >= 2 && args[0]?.toLowerCase() === '/p') {
            const setPromptInline = parseSetPromptCommand(trimmedInput);
            if (!setPromptInline) {
              output = [{ text: 'Usage: set /p VAR=Prompt text', type: 'warning' }];
            } else {
              queueTerminalInput(setPromptInline.prompt, async (value: string) => {
                env[setPromptInline.variableName] = value;
              });
              output = [{ text: `Input requested for %${setPromptInline.variableName}%`, type: 'info' }];
            }
            break;
          }

          if (args.length === 0) {
            const entries = Object.entries(env);
            if (entries.length === 0) {
              output = [{ text: 'No environment variables set.', type: 'info' }];
            } else {
              output = [{ text: 'Environment variables:', type: 'info' }];
              entries.forEach(([k, v]) => output.push({ text: `${k}=${v}`, type: 'normal' }));
            }
          } else {
            const kv = args.join(' ');
            const idx = kv.indexOf('=');
            if (idx === -1) {
              output = [{ text: 'Usage: set KEY=VALUE', type: 'warning' }];
            } else {
              const key = kv.slice(0, idx).trim();
              const val = kv.slice(idx + 1).trim();
              env[key] = val;
              output = [{ text: `Set ${key}=${val}`, type: 'success' }];
              if (key.toUpperCase() === 'PATH') {
                output.push({ text: `PATH updated: ${val}`, type: 'info' });
              }
            }
          }
          break;
        }

        case 'call': {
          // call script.bat or script.sh
          if (args.length === 0) {
            output = [{ text: 'Usage: call <script.bat|script.sh>', type: 'warning' }];
          } else {
            const target = args[0];
            const ext = (target.split('.').pop() || '').toLowerCase();
            if (ext === 'bat' || ext === 'cmd') {
              await runScriptFromPath(target, 'bat');
              setIsProcessing(false);
              return [{ text: `Executed script: ${target}`, type: 'success' }];
            } else if (ext === 'sh') {
              await runScriptFromPath(target, 'sh');
              setIsProcessing(false);
              return [{ text: `Executed script: ${target}`, type: 'success' }];
            } else {
              output = [{ text: `Unsupported script type: ${target}`, type: 'error' }];
            }
          }
          break;
        }

        case 'help':
          output = [
            { text: 'Available commands:', type: 'info' },
            { text: '  cls/clear     - Clear the terminal screen', type: 'normal' },
            { text: '  dir/ls        - List directory contents', type: 'normal' },
            { text: '  cd <path>     - Change current directory', type: 'normal' },
            { text: '  type <file>   - Display file contents', type: 'normal' },
            { text: '  mkdir <name>  - Create a new directory', type: 'normal' },
            { text: '  rmdir <path>  - Remove a directory (moves to Recycle Bin)', type: 'normal' },
            { text: '  copy <src> <dest>      - Copy a file', type: 'normal' },
            { text: '  xcopy <src> <dest>     - Copy folders recursively', type: 'normal' },
            { text: '  robocopy <src> <dest>  - Robust recursive copy (simulated)', type: 'normal' },
            { text: '  del/erase <file>       - Delete a file (moves to Recycle Bin)', type: 'normal' },
            { text: '  move <src> <dest>      - Move/rename file or folder', type: 'normal' },
            { text: '  echo <text>   - Display text', type: 'normal' },
            { text: '  input <prompt> - Read input into %INPUT%', type: 'normal' },
            { text: '  set /p V=Prompt - Read input into environment variable V', type: 'normal' },
            { text: '  pwd           - Show current directory', type: 'normal' },
            { text: '  tree          - Show directory tree', type: 'normal' },
            { text: '  ver           - Show system version', type: 'normal' },
            { text: '  systeminfo    - Detailed system info (simulated)', type: 'normal' },
            { text: '  hostname      - Show computer name', type: 'normal' },
            { text: '  tasklist      - List open windows (processes)', type: 'normal' },
            { text: '  taskkill      - End a task by PID/title (simulated)', type: 'normal' },
            { text: '  msinfo32      - Open System Information', type: 'normal' },
            { text: '  chkdsk        - Check disk (VFS usage)', type: 'normal' },
            { text: '  sfc /scannow  - Scan system files (simulated)', type: 'normal' },
            { text: '  ipconfig      - Network configuration (simulated)', type: 'normal' },
            { text: '  ping <host>   - Ping a host (HTTP timing)', type: 'normal' },
            { text: '  tracert <host>- Trace route (simulated)', type: 'normal' },
            { text: '  netstat       - Active connections (simulated)', type: 'normal' },
            { text: '  nslookup <host> - DNS lookup (simulated)', type: 'normal' },
            { text: '  ssh user@host - Start an in-place SSH session in this terminal', type: 'normal' },
            { text: '  ssh profile-name - Connect using a saved SSH profile', type: 'normal' },
            { text: '  ssh-config    - Launch the Terminal SSH profile manager', type: 'normal' },
            { text: '  set [KEY=VALUE] - Manage environment variables', type: 'normal' },
            { text: '  call <script> - Run a .bat or .sh script', type: 'normal' },
            { text: '  <script.bat>  - Run a batch script by typing its name', type: 'normal' },
            { text: '  mapleclaw     - MapleClaw control-plane terminal commands', type: 'normal' },
            { text: '  maplecode     - Launch the MapleCode full-screen AI REPL', type: 'normal' },
            { text: '  demythos      - Launch the DeMythos looped reasoning console', type: 'normal' },
            { text: '  top           - MapleOS WASM process viewer (Ctrl+C to exit)', type: 'normal' },
            { text: '  exit          - Close terminal', type: 'normal' },
          ];
          break;

        case 'cls':
        case 'clear':
          setHistory([]);
          setIsProcessing(false);
          return [];

        case 'dir':
        case 'ls':
          if (navigator) {
            const items = navigator.getItemsInPath(currentPath);
            if (items.length === 0) {
              output = [{ text: 'Directory is empty.', type: 'info' }];
            } else {
              output = [
                { text: `Directory of ${currentPath}`, type: 'path' },
                { text: '', type: 'normal' }
              ];
              
              items.forEach(item => {
                const date = formatDate(item.modified);
                const size = item.type === 'file' ? formatFileSize(item.size) : '<DIR>';
                const line = `${date.padEnd(20)} ${size.padEnd(12)} ${item.name}`;
                output.push({ 
                  text: line, 
                  type: item.type === 'folder' ? 'folder' : 'file' 
                });
              });
              
              output.push({ text: '', type: 'normal' });
              output.push({ 
                text: `${items.filter(i => i.type === 'file').length} File(s), ${items.filter(i => i.type === 'folder').length} Dir(s)`, 
                type: 'info' 
              });
            }
          } else {
            output = [{ text: 'File system not available.', type: 'error' }];
          }
          break;

        case 'cd':
          if (args.length === 0) {
            output = [{ text: currentPath, type: 'path' }];
          } else {
            const newPath = args[0];
            if (navigator) {
              let targetPath: string;
              if (newPath === '..') {
                const pathParts = currentPath.split('\\').filter(Boolean);
                if (pathParts.length <= 1) {
                  targetPath = 'C:\\';
                } else {
                  targetPath = pathParts.slice(0, -1).join('\\');
                  if (!targetPath.match(/^[A-Z]:$/)) {
                    targetPath = targetPath + '\\';
                  }
                }
              } else if (newPath.startsWith('C:\\') || newPath.startsWith('c:\\')) {
                targetPath = newPath.replace(/^c:\\/i, 'C:\\');
              } else {
                targetPath = currentPath === 'C:\\' ? `C:\\${newPath}` : `${currentPath}\\${newPath}`;
              }

              const item = navigator.findItemByPath(targetPath);
              if (item && item.type === 'folder') {
                setCurrentPath(targetPath);
                output = [{ text: '', type: 'normal' }];
              } else {
                output = [{ text: 'The system cannot find the path specified.', type: 'error' }];
              }
            } else {
              output = [{ text: 'File system not available.', type: 'error' }];
            }
          }
          break;

        case 'type':
        case 'cat':
          if (args.length === 0) {
            output = [{ text: `Usage: ${command} <filename>`, type: 'warning' }];
          } else if (navigator) {
            const fileName = args[0];
            const filePath = currentPath === 'C:\\' ? `C:\\${fileName}` : `${currentPath}\\${fileName}`;
            const item = navigator.findItemByPath(filePath);
            if (item && item.type === 'file') {
              output = [{ text: item.content || '', type: 'normal' }];
            } else {
              output = [{ text: 'The system cannot find the file specified.', type: 'error' }];
            }
          } else {
            output = [{ text: 'File system not available.', type: 'error' }];
          }
          break;

        case 'mkdir':
          if (args.length === 0) {
            output = [{ text: 'Usage: mkdir <directory_name>', type: 'warning' }];
          } else if (navigator) {
            try {
              const dirName = args[0];
              const newPath = currentPath === 'C:\\' ? `C:\\${dirName}` : `${currentPath}\\${dirName}`;
              await navigator.createFolder(newPath);

              onFileSystemChange?.();
              output = [{ text: `Directory created: ${dirName}`, type: 'success' }];
            } catch (error) {
              output = [{ text: `Error creating directory: ${error}`, type: 'error' }];
            }
          } else {
            output = [{ text: 'File system not available.', type: 'error' }];
          }
          break;

        case 'echo':
          output = [{ text: args.join(' '), type: 'normal' }];
          break;

        case 'pwd':
          output = [{ text: currentPath, type: 'path' }];
          break;

        case 'tree':
          if (navigator) {
            const buildTree = (path: string, prefix = '', depth = 0): TerminalOutput[] => {
              if (depth > 5) return [];
              const items = navigator.getItemsInPath(path);
              const result: TerminalOutput[] = [];
              
              items.forEach((item, index) => {
                const isLast = index === items.length - 1;
                const connector = isLast ? '└── ' : '├── ';
                result.push({
                  text: `${prefix}${connector}${item.name}`,
                  type: item.type === 'folder' ? 'folder' : 'file'
                });
                
                if (item.type === 'folder') {
                  const childPrefix = prefix + (isLast ? '    ' : '│   ');
                  result.push(...buildTree(item.path, childPrefix, depth + 1));
                }
              });
              
              return result;
            };
            
            output = [
              { text: currentPath, type: 'path' },
              ...buildTree(currentPath)
            ];
          } else {
            output = [{ text: 'File system not available.', type: 'error' }];
          }
          break;

        case 'ver':
          output = [
            { text: 'MapleOS [Version 1.0.0]', type: 'info' },
            { text: 'A web-based desktop environment', type: 'normal' }
          ];
          break;

        case 'time':
          output = [{ text: new Date().toLocaleTimeString(), type: 'info' }];
          break;

        case 'top':
          startTop();
          setIsProcessing(false);
          return [];

        case 'maplecode':
          addOutput('maplecode', [{ text: 'Launching MapleCode...', type: 'info' }]);
          setInteractiveApp('maplecode');
          setIsProcessing(false);
          return [{ text: 'Launching MapleCode...', type: 'info' }];

        case 'demythos':
          addOutput('demythos', [{ text: 'Launching DeMythos...', type: 'info' }]);
          setInteractiveApp('demythos');
          setIsProcessing(false);
          return [{ text: 'Launching DeMythos...', type: 'info' }];

        case 'ssh-config':
        case 'ssh-profiles':
          addOutput(command, [{ text: 'Launching SSH Config...', type: 'info' }]);
          setInteractiveApp('ssh-config');
          setIsProcessing(false);
          return [{ text: 'Launching SSH Config...', type: 'info' }];

        case 'ssh': {
          if (!isElectronSshAvailable) {
            output = [{ text: 'SSH is only available in the MapleOS desktop app.', type: 'warning' }];
            break;
          }

          if (activeSshSessionRef.current) {
            output = [{ text: 'An SSH session is already active in this terminal.', type: 'warning' }];
            break;
          }

          const parsed = parseSshCommandArgs(args);
          if (parsed.errors.length) {
            output = parsed.errors.map((text) => ({ text, type: 'warning' as const }));
            break;
          }

          const destination = parsed.destination || '';
          const namedProfile = findSshProfileByReference(sshProfiles, destination);
          const identityProfile = parsed.identity ? findSshProfileByReference(sshProfiles, parsed.identity) : null;

          if (parsed.identity && !identityProfile) {
            output = [{ text: `Could not find SSH profile: ${parsed.identity}`, type: 'error' }];
            break;
          }

          if (namedProfile && !parsed.identity) {
            await beginSshConnectionFlow(input, {
              host: namedProfile.host,
              port: parsed.port || namedProfile.port,
              username: namedProfile.username,
              authType: namedProfile.authType,
              privateKey: namedProfile.privateKey,
              profileId: namedProfile.id,
            });
            output = [];
            break;
          }

          const { username, host } = splitSshDestination(destination);
          if (!host) {
            output = [{ text: 'Usage: ssh [-p port] [-i profile] user@host | host | profile-name', type: 'warning' }];
            break;
          }

          if (identityProfile && identityProfile.authType !== 'privateKey') {
            output = [{ text: 'The -i option expects a saved private-key SSH profile.', type: 'error' }];
            break;
          }

          const matchedProfile = identityProfile || findMatchingSshProfile(sshProfiles, {
            host,
            username,
            port: parsed.port || null,
          });

          if (matchedProfile) {
            output = [{
              text: `Using saved SSH profile ${matchedProfile.name} for ${matchedProfile.username}@${matchedProfile.host}:${parsed.port || matchedProfile.port}.`,
              type: 'info',
            }];
          }

          await beginSshConnectionFlow(input, {
            host,
            port: parsed.port || matchedProfile?.port || identityProfile?.port || 22,
            username: username || matchedProfile?.username || identityProfile?.username || null,
            authType: matchedProfile?.authType || (identityProfile ? 'privateKey' : 'password'),
            privateKey: matchedProfile?.privateKey || identityProfile?.privateKey,
            profileId: matchedProfile?.id || identityProfile?.id,
          });
          break;
        }

        case 'exit':
          output = [{ text: 'Goodbye!', type: 'info' }];
          break;

        case 'copy': {
          if (args.length < 2) {
            output = [{ text: 'Usage: copy <source> <destination>', type: 'warning' }];
            break;
          }
          if (!navigator) {
            output = [{ text: 'File system not available.', type: 'error' }];
            break;
          }
          try {
            const [srcArg, destArg] = args;
            const src = srcArg.match(/^[a-z]:\\/i) ? srcArg.replace(/^c:\\/i, 'C:\\') : (currentPath === 'C:\\' ? `C:\\${srcArg}` : `${currentPath}\\${srcArg}`);
            const dest = destArg.match(/^[a-z]:\\/i) ? destArg.replace(/^c:\\/i, 'C:\\') : (currentPath === 'C:\\' ? `C:\\${destArg}` : `${currentPath}\\${destArg}`);
            const srcItem = navigator.findItemByPath(src);
            if (!srcItem || srcItem.type !== 'file') {
              output = [{ text: 'The system cannot find the file specified.', type: 'error' }];
              break;
            }
            await navigator.createFile(dest, srcItem.content || '');

            onFileSystemChange?.();
            output = [{ text: `        1 file(s) copied.`, type: 'success' }];
          } catch (error) {
            output = [{ text: `Error copying file: ${error}`, type: 'error' }];
          }
          break;
        }

        case 'xcopy':
        case 'robocopy': {
          if (args.length < 2) {
            output = [{ text: `${command.toUpperCase()} completed (simulated recursive copy).`, type: 'success' }];
            break;
          }
          if (!navigator) {
            output = [{ text: 'File system not available.', type: 'error' }];
            break;
          }
          output = [{ text: `${command.toUpperCase()} completed (simulated recursive copy).`, type: 'success' }];
          break;
        }

        case 'del':
        case 'erase': {
          if (args.length < 1) {
            output = [{ text: `Usage: ${command} <file>`, type: 'warning' }];
            break;
          }
          if (!navigator) {
            output = [{ text: 'File system not available.', type: 'error' }];
            break;
          }
          try {
            const p = args[0].match(/^[a-z]:\\/i) ? args[0].replace(/^c:\\/i, 'C:\\') : (currentPath === 'C:\\' ? `C:\\${args[0]}` : `${currentPath}\\${args[0]}`);
            const ok = navigator.deleteItem(p);
            if (!ok) {
              output = [{ text: 'Could Not Find File', type: 'error' }];
              break;
            }
            onFileSystemChange?.();
            output = [{ text: '        1 file(s) moved to Recycle Bin.', type: 'success' }];
          } catch (error) {
            output = [{ text: `Error deleting file: ${error}`, type: 'error' }];
          }
          break;
        }

        case 'move': {
          if (args.length < 2) {
            output = [{ text: 'Usage: move <source> <destination>', type: 'warning' }];
            break;
          }
          if (!navigator) {
            output = [{ text: 'File system not available.', type: 'error' }];
            break;
          }
          try {
            const [srcArg, destArg] = args;
            const src = srcArg.match(/^[a-z]:\\/i) ? srcArg.replace(/^c:\\/i, 'C:\\') : (currentPath === 'C:\\' ? `C:\\${srcArg}` : `${currentPath}\\${srcArg}`);
            const dest = destArg.match(/^[a-z]:\\/i) ? destArg.replace(/^c:\\/i, 'C:\\') : (currentPath === 'C:\\' ? `C:\\${destArg}` : `${currentPath}\\${destArg}`);
            const srcItem = navigator.findItemByPath(src);
            if (!srcItem) {
              output = [{ text: 'The system cannot find the file specified.', type: 'error' }];
              break;
            }
            if (srcItem.type === 'file') {
              await navigator.createFile(dest, srcItem.content || '');
            } else {
              await navigator.createFolder(dest);
            }

            navigator.deleteItem(src);
            onFileSystemChange?.();
            output = [{ text: '        1 item(s) moved.', type: 'success' }];
          } catch (error) {
            output = [{ text: `Error moving item: ${error}`, type: 'error' }];
          }
          break;
        }

        case 'rmdir': {
          if (args.length < 1) {
            output = [{ text: 'Usage: rmdir <directory>', type: 'warning' }];
            break;
          }
          if (!navigator) {
            output = [{ text: 'File system not available.', type: 'error' }];
            break;
          }
          try {
            const p = args[0].match(/^[a-z]:\\/i) ? args[0].replace(/^c:\\/i, 'C:\\') : (currentPath === 'C:\\' ? `C:\\${args[0]}` : `${currentPath}\\${args[0]}`);
            const ok = navigator.deleteItem(p);
            if (!ok) {
              output = [{ text: 'The system cannot find the path specified.', type: 'error' }];
              break;
            }
            onFileSystemChange?.();
            output = [{ text: 'Directory moved to Recycle Bin.', type: 'success' }];
          } catch (error) {
            output = [{ text: `Error removing directory: ${error}`, type: 'error' }];
          }
          break;
        }

        case 'attrib': {
          output = [{ text: 'ATTRIB is simulated in MapleOS. No attributes are currently tracked.', type: 'info' }];
          break;
        }

        case 'systeminfo': {
          const isElectron = typeof window !== 'undefined' && Boolean((window as any).electronAPI?.isElectron);
          const ua = currentPath;
          output = [
            { text: 'Host Name:                 MAPLEOS', type: 'normal' },
            { text: 'OS Name:                   MapleOS', type: 'normal' },
            { text: 'OS Version:                1.0.0', type: 'normal' },
            { text: `System Type:               ${isElectron ? 'Electron Desktop' : 'Web Browser'}`, type: 'normal' },
            { text: `Current Directory:         ${ua}`, type: 'normal' },
            { text: `Local Time:                ${new Date().toLocaleString()}`, type: 'normal' },
          ];
          break;
        }

        case 'hostname': {
          output = [{ text: 'MAPLEOS', type: 'normal' }];
          break;
        }

        case 'tasklist': {
          const snapshot = Object.values(getActivitySnapshot());
          output = [
            { text: 'Image Name                   PID Session Name        Session#    Mem Usage', type: 'info' },
            { text: '========================= ======== ================ =========== ============', type: 'info' },
            ...snapshot.slice(0, 20).map((item, idx) => ({
              text: `${String(item.appId).slice(0, 24).padEnd(24)} ${String(1000 + idx).padStart(8)} Console                 1      ${Math.round(item.pct).toString().padStart(4)} K`,
              type: 'normal' as const
            }))
          ];
          break;
        }

        case 'taskkill': {
          output = [{ text: 'TASKKILL is simulated in MapleOS. Use window controls to close apps.', type: 'warning' }];
          break;
        }

        case 'msinfo32': {
          output = [{ text: 'Opening System Information...', type: 'info' }];
          break;
        }

        case 'driverquery': {
          output = [
            { text: 'Module Name           Display Name                    Driver Type   Link Date', type: 'info' },
            { text: '==================== ============================== ============ =================', type: 'info' },
            { text: 'browser-sim          Browser Runtime                 Kernel       n/a', type: 'normal' },
            { text: 'vfs-sim              MapleOS Virtual FS              FileSystem    n/a', type: 'normal' },
          ];
          break;
        }

        case 'chkdsk': {
          output = [
            { text: 'The type of the file system is MapleFS.', type: 'normal' },
            { text: 'Drive scan complete.', type: 'normal' },
            { text: 'Windows has scanned the file system and found no problems.', type: 'success' },
          ];
          break;
        }

        case 'sfc': {
          const scanArg = args.join(' ').toLowerCase();
          if (scanArg !== '/scannow') {
            output = [{ text: 'Usage: sfc /scannow', type: 'warning' }];
          } else {
            output = [
              { text: 'Beginning system scan. This process will take some time.', type: 'normal' },
              { text: 'Verification 100% complete.', type: 'normal' },
              { text: 'Windows Resource Protection did not find any integrity violations.', type: 'success' },
            ];
          }
          break;
        }

        case 'ipconfig': {
          const host = globalThis.location?.hostname || 'localhost';
          output = [
            { text: 'Windows IP Configuration (simulated)', type: 'info' },
            { text: `Host Name . . . . . . . . . . . . : ${host}`, type: 'normal' },
            { text: `IPv4 Address. . . . . . . . . . . : n/a (browser)`, type: 'normal' },
            { text: `Subnet Mask . . . . . . . . . . . : n/a`, type: 'normal' },
            { text: `Default Gateway . . . . . . . . . : n/a`, type: 'normal' }
          ];
          break;
        }

        case 'ping': {
          if (args.length < 1) {
            output = [{ text: 'Usage: ping <host>', type: 'warning' }];
            break;
          }
          const target = args[0].replace(/^https?:\/\//, '');
          const url = `https://${target}`;
          const attempts = 4;
          const lines: TerminalOutput[] = [{ text: `Pinging ${target} with HTTP GET (simulated):`, type: 'info' }];
          for (let i = 0; i < attempts; i++) {
            const start = performance.now();
            try {
              await fetch(url, { mode: 'no-cors' });
              const ms = Math.round(performance.now() - start);
              lines.push({ text: `Reply from ${target}: time=${ms}ms`, type: 'normal' });
            } catch {
              const ms = Math.round(performance.now() - start);
              lines.push({ text: `Request timed out. (${ms}ms)`, type: 'warning' });
            }
          }
          output = lines;
          break;
        }

        case 'tracert': {
          if (args.length < 1) {
            output = [{ text: 'Usage: tracert <host>', type: 'warning' }];
            break;
          }
          const host = args[0];
          output = [
            { text: `Tracing route to ${host} over a maximum of 30 hops (simulated)`, type: 'info' },
            { text: `  1   *  *  *   Request timed out.`, type: 'normal' },
            { text: `  2   CDN Gateway (simulated)`, type: 'normal' },
            { text: `  3   ${host} (final)`, type: 'normal' }
          ];
          break;
        }

        case 'netstat': {
          output = [
            { text: 'Active Connections (simulated)', type: 'info' },
            { text: 'Proto  Local Address          Foreign Address        State', type: 'normal' },
            { text: 'TCP    127.0.0.1:443          example.com:https      ESTABLISHED', type: 'normal' },
            { text: 'TCP    127.0.0.1:443          cdn.example:https      ESTABLISHED', type: 'normal' }
          ];
          break;
        }

        case 'nslookup': {
          if (args.length < 1) {
            output = [{ text: 'Usage: nslookup <host>', type: 'warning' }];
            break;
          }
          output = [
            { text: `Server:  dns.simulated.local`, type: 'normal' },
            { text: `Address:  127.0.0.1`, type: 'normal' },
            { text: `\nNon-authoritative answer:`, type: 'normal' },
            { text: `${args[0]}    A     93.184.216.34 (simulated)`, type: 'normal' }
          ];
          break;
        }

        case 'mapleclaw': {
          const result = await executeMapleClawCli(args);
          if (result.openWindow && onOpenWindow) {
            onOpenWindow(result.openWindow.appId, result.openWindow.title);
          }
          output = result.output;
          break;
        }

        case 'net':
        case 'netsh':
        case 'arp':
        case 'route':
        case 'telnet': {
          output = [{ text: `${command} is not available in the browser environment (simulated).`, type: 'warning' }];
          break;
        }

        default: {
          // Direct script invocation: "run.bat" or "script.sh"
          if (/\.(bat|cmd)$/i.test(command)) {
            await runScriptFromPath(command, 'bat');
            setIsProcessing(false);
            return [{ text: `Executed script: ${command}`, type: 'success' }];
          }
          if (/\.(sh)$/i.test(command)) {
            await runScriptFromPath(command, 'sh');
            setIsProcessing(false);
            return [{ text: `Executed script: ${command}`, type: 'success' }];
          }

          // Try launching a MapleOS program by id or name
          const program = resolveProgram(command);
          if (program && onOpenWindow) {
            const pathArg = args[0]; // optional path
            onOpenWindow(program.id, program.label, pathArg);
            output = [
              { text: `Launching ${program.label}${pathArg ? ` with path "${pathArg}"` : ''}...`, type: 'info' }
            ];
          } else {
            output = [{ 
              text: `'${command}' is not recognized as an internal or external command, operable program or batch file.`, 
              type: 'error' 
            }];
          }
          break;
        }
      }
    } catch (error) {
      output = [{ text: `Error executing command: ${error}`, type: 'error' }];
    }

    addOutput(input, output);
    setIsProcessing(false);
    return output;
  };

  const runTerminalCommandForMapleCode = async (input: string) => {
    const trimmed = input.trim();
    if (!trimmed) {
      return { kind: 'output', text: 'Usage: /cmd <terminal command>' } as MapleCodeTerminalBridgeResult;
    }

    const expandedInput = expandEnvironmentVariables(trimmed);
    const directSetPrompt = parseSetPromptCommand(expandedInput);
    if (directSetPrompt) {
      pendingMapleCodeInputRef.current = async (value: string) => {
        const env = getMapleOsEnv();
        env[directSetPrompt.variableName] = value;
        return { kind: 'output', text: `Input saved to %${directSetPrompt.variableName}%` };
      };
      return { kind: 'input', prompt: directSetPrompt.prompt } as MapleCodeTerminalBridgeResult;
    }

    const { command, args } = parseCommand(expandedInput);
    if (command === 'input') {
      const prompt = args.join(' ').trim() || 'Input:';
      pendingMapleCodeInputRef.current = async (value: string) => {
        const env = getMapleOsEnv();
        env.INPUT = value;
        return { kind: 'output', text: `Input saved to %INPUT%` };
      };
      return { kind: 'input', prompt } as MapleCodeTerminalBridgeResult;
    }

    if (command === 'call' && args[0] && /\.(bat|cmd)$/i.test(args[0])) {
      return runScriptFromPathForMapleCode(args[0], 'bat');
    }
    if (command === 'call' && args[0] && /\.sh$/i.test(args[0])) {
      return runScriptFromPathForMapleCode(args[0], 'sh');
    }
    if (/\.(bat|cmd)$/i.test(command)) {
      return runScriptFromPathForMapleCode(command, 'bat');
    }
    if (/\.sh$/i.test(command)) {
      return runScriptFromPathForMapleCode(command, 'sh');
    }

    const activeSession = activeSshSessionRef.current;

    if (activeSession?.status === 'connected' && command === 'ssh') {
      const parsedSsh = parseSshCommandArgs(args);
      const destination = parsedSsh.destination || '';
      const namedProfile = findSshProfileByReference(sshProfiles, destination);
      const { username, host } = splitSshDestination(destination);
      const isCurrentRemoteTarget = Boolean(
        host
        && host.toLowerCase() === activeSession.host.toLowerCase()
        && (!username || username.toLowerCase() === activeSession.username.toLowerCase())
      );

      if (namedProfile || isCurrentRemoteTarget) {
        const targetLabel = `${activeSession.username}@${activeSession.host}:${activeSession.port}`;
        return {
          kind: 'output',
          text: [
            `You are already connected to ${targetLabel}.`,
            'Commands from DeMythos are currently executing inside the remote shell, so MapleOS SSH profile aliases such as this one should not be run again here.',
            'Run the remote shell command directly, or use exit first if you need to return to MapleOS Terminal before reconnecting.',
          ].join(' '),
        } as MapleCodeTerminalBridgeResult;
      }
    }

    if (activeSession?.status === 'connected' && window.electronAPI?.ssh) {
      return new Promise<MapleCodeTerminalBridgeResult>((resolve) => {
        setIsAssistantBridgeBusy(true);
        pendingBridgeSshOperationRef.current = {
          resolve,
          chunks: [],
          fallbackText: `Command sent to ${activeSession.username}@${activeSession.host}.`,
          idleTimer: null,
        };

        void window.electronAPI?.ssh?.sendInput({
          sessionId: activeSession.sessionId,
          input: `${expandedInput}\n`,
        }).then((sent) => {
          if (!sent) {
            resolvePendingBridgeSshOperation({ kind: 'output', text: `Failed to send command to ${activeSession.username}@${activeSession.host}.` });
          } else {
            schedulePendingBridgeSshOutputFlush();
          }
        });
      });
    }

    if (command === 'ssh' && isElectronSshAvailable) {
      return new Promise<MapleCodeTerminalBridgeResult>(async (resolve) => {
        setIsAssistantBridgeBusy(true);
        pendingBridgeSshOperationRef.current = {
          resolve,
          chunks: [],
          fallbackText: `SSH command started: ${expandedInput}`,
          idleTimer: null,
        };

        const output = await executeCommand(expandedInput);
        if (output.length) {
          resolvePendingBridgeSshOperation({ kind: 'output', text: output.map((line) => line.text).join('\n') });
          return;
        }

        schedulePendingBridgeSshOutputFlush();
      });
    }

    const output = await executeCommand(expandedInput);
    if (!output.length) {
      return { kind: 'output', text: `Command executed: ${input}` } as MapleCodeTerminalBridgeResult;
    }

    return { kind: 'output', text: output.map((line) => line.text).join('\n') } as MapleCodeTerminalBridgeResult;
  };

  const submitTerminalInputForMapleCode = async (input: string): Promise<MapleCodeTerminalBridgeResult> => {
    const resolver = pendingMapleCodeInputRef.current;
    if (resolver) {
      pendingMapleCodeInputRef.current = null;
      return resolver(input);
    }

    const sshPromptResolver = pendingSshPromptRef.current;
    if (sshPromptResolver) {
      return new Promise<MapleCodeTerminalBridgeResult>((resolve) => {
        pendingBridgeSshOperationRef.current = {
          resolve,
          chunks: [],
          fallbackText: 'SSH input submitted.',
          idleTimer: null,
        };

        void sshPromptResolver(input)
          .then(() => {
            schedulePendingBridgeSshOutputFlush();
          })
          .catch((error) => {
            resolvePendingBridgeSshOperation({ kind: 'output', text: error instanceof Error ? error.message : 'SSH input failed.' });
          });
      });
    }

    return { kind: 'output', text: 'No pending terminal input.' };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reverseSearchActive) {
      clearReverseSearch(false);
      setHistoryCursor(-1);
      return;
    }

    if (pendingSshPrompt && pendingSshPromptRef.current) {
      const answer = inputValue;
      const submitPendingPrompt = pendingSshPromptRef.current;
      const displayValue = pendingSshPrompt.secret ? '[hidden]' : answer;
      addOutput('', [{ text: `${pendingSshPrompt.prompt}${displayValue}`, type: 'info' }]);
      setInputValue('');
      setIsProcessing(true);
      void submitPendingPrompt(answer)
        .catch((error) => {
          addOutput('', [{ text: `SSH prompt failed: ${error}`, type: 'error' }]);
        })
        .finally(() => {
          setIsProcessing(false);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        });
      return;
    }

    if (pendingTerminalInputPrompt && pendingTerminalInputRef.current) {
      const answer = inputValue;
      const submitPendingInput = pendingTerminalInputRef.current;
      addOutput(answer, []);
      setInputValue('');
      setIsProcessing(true);
      void submitPendingInput(answer)
        .catch((error) => {
          addOutput('', [{ text: `Error reading input: ${error}`, type: 'error' }]);
        })
        .finally(() => {
          setIsProcessing(false);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        });
      return;
    }

    if (activeSshSessionRef.current && window.electronAPI?.ssh && !isProcessing) {
      const sshInput = inputValue;
      const activeSession = activeSshSessionRef.current;
      const normalizedSshInput = sshInput.trim().toLowerCase();
      addOutput(sshInput, []);
      setInputValue('');

      if (normalizedSshInput === 'exit' && activeSession.status !== 'connected') {
        void disconnectActiveSshSession('user');
        return;
      }

      if (activeSession.status === 'connected') {
        void window.electronAPI.ssh.sendInput({
          sessionId: activeSession.sessionId,
          input: `${sshInput}\n`,
        }).then((sent) => {
          if (!sent && normalizedSshInput === 'exit') {
            void disconnectActiveSshSession('user');
          }
        });
      }
      return;
    }

    if (inputValue.trim() && !isProcessing) {
      const trimmedCommand = inputValue.trim();
      const nextHistory = [...commandHistoryRef.current.filter((command) => command !== trimmedCommand), trimmedCommand];

      setCommandHistoryState(nextHistory);
      historyDraftRef.current = '';
      setHistoryCursor(-1);
      
      executeCommand(trimmedCommand);
      setInputValue('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (reverseSearchActive) return;
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (activeSshSessionRef.current && e.ctrlKey && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      interruptActiveSshSession();
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      clearReverseSearch(false);
      handleTabCompletion();
      return;
    }

    if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'r') {
      e.preventDefault();
      const nextQuery = reverseSearchActive ? reverseSearchQuery : inputValue.trim();

      if (!reverseSearchActive) {
        reverseSearchDraftRef.current = inputValue;
      }

      updateReverseSearch(
        nextQuery,
        reverseSearchActive
          ? reverseSearchMatchIndex > 0
            ? reverseSearchMatchIndex - 1
            : -1
          : undefined
      );
      return;
    }

    if (reverseSearchActive) {
      if (e.key === 'Escape') {
        e.preventDefault();
        clearReverseSearch(true);
        setHistoryCursor(-1);
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        clearReverseSearch(false);
        setHistoryCursor(-1);
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        updateReverseSearch(reverseSearchQuery.slice(0, -1));
        return;
      }

      if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) {
        e.preventDefault();
        updateReverseSearch(`${reverseSearchQuery}${e.key}`);
        return;
      }
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      clearReverseSearch(false);
      const historyItems = commandHistoryRef.current;
      if (historyItems.length === 0) return;

      if (historyIndexRef.current === -1) {
        historyDraftRef.current = inputValue;
      }

      const nextIndex = historyIndexRef.current === -1
        ? historyItems.length - 1
        : Math.max(0, historyIndexRef.current - 1);

      setHistoryCursor(nextIndex);
      setInputValue(historyItems[nextIndex] || '');
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      clearReverseSearch(false);
      const historyItems = commandHistoryRef.current;
      if (historyItems.length === 0 || historyIndexRef.current === -1) return;

      const nextIndex = historyIndexRef.current + 1;
      if (nextIndex >= historyItems.length) {
        setHistoryCursor(-1);
        setInputValue(historyDraftRef.current);
        return;
      }

      setHistoryCursor(nextIndex);
      setInputValue(historyItems[nextIndex] || '');
    }
  };

  const getOutputColor = (type: TerminalOutput['type']): string => {
    switch (type) {
      case 'error': return 'text-red-400';
      case 'success': return 'text-green-400';
      case 'warning': return 'text-yellow-400';
      case 'info': return 'text-cyan-400';
      case 'path': return 'text-blue-400';
      case 'file': return 'text-white';
      case 'folder': return 'text-yellow-300';
      default: return 'text-gray-300';
    }
  };

  const getTextColor = (): string => {
    switch (terminalSettings.textColor) {
      case 'white': return 'text-white';
      case 'cyan': return 'text-cyan-400';
      case 'yellow': return 'text-yellow-400';
      case 'orange': return 'text-orange-400';
      default: return 'text-green-400';
    }
  };

  const getTextColorValue = (): string => {
    switch (terminalSettings.textColor) {
      case 'white': return '#ffffff';
      case 'cyan': return '#22d3ee';
      case 'yellow': return '#facc15';
      case 'orange': return '#fb923c';
      default: return '#4ade80';
    }
  };

  const getCursorClass = (): string => {
    switch (terminalSettings.cursorStyle) {
      case 'underline': return 'caret-green-400 border-b border-green-400';
      case 'bar': return 'caret-green-400 border-l-2 border-green-400';
      default: return 'caret-green-400';
    }
  };

  const promptText = activeSshSession
    ? `ssh:${activeSshSession.username}@${activeSshSession.host}${activeSshSession.status === 'connecting' ? ' (connecting)' : ''}>`
    : `${currentPath}>`;
  const isSshTerminalActive = activeSshSession?.status === 'connected' && !sshViewportFailed;
  const activeDeMythosRun = useMemo(
    () => deMythosRuns.find((run) => run.status === 'running' || run.status === 'paused') || null,
    [deMythosRuns]
  );
  const deMythosSessionRun = useMemo(() => {
    if (!activeSshSession) {
      return activeDeMythosRun;
    }

    const matchesActiveSession = (run: MapleMythosRunState) => Boolean(
      run.sshGrant
      && run.sshGrant.host.toLowerCase() === activeSshSession.host.toLowerCase()
      && run.sshGrant.port === activeSshSession.port
      && run.sshGrant.username.toLowerCase() === activeSshSession.username.toLowerCase()
    );

    if (activeDeMythosRun && matchesActiveSession(activeDeMythosRun)) {
      return activeDeMythosRun;
    }

    return deMythosRuns.find(matchesActiveSession) || activeDeMythosRun;
  }, [activeDeMythosRun, activeSshSession, deMythosRuns]);
  const deMythosOwnsActiveRemoteSession = Boolean(deMythosSessionRun && activeSshSession);
  const shouldReturnToDeMythos = Boolean(
    interactiveApp === 'demythos'
    && deMythosOwnsActiveRemoteSession
    && !isAssistantBridgeBusy
    && !pendingSshPrompt
  );
  const shouldShowSshViewport = isSshTerminalActive && !shouldReturnToDeMythos;
  const deMythosTerminalStatus = useMemo(
    () => deMythosOwnsActiveRemoteSession
      ? buildDeMythosTerminalStatus(deMythosSessionRun, {
          isBridgeBusy: isAssistantBridgeBusy,
          waitingForPrompt: Boolean(pendingSshPrompt),
        })
      : null,
    [deMythosOwnsActiveRemoteSession, deMythosSessionRun, isAssistantBridgeBusy, pendingSshPrompt]
  );

  // Auto-run when Terminal is opened with a .bat or .sh file
  useEffect(() => {
    if (autoRunRef.current) return;

    const maybeAutoRun = async () => {
      if (filePath === MAPLECODE_ASSISTANT_BOOT_PATH) {
        autoRunRef.current = true;
        addOutput('maplecode', [{ text: 'Launching MapleCode...', type: 'info' }]);
        setInteractiveApp('maplecode');
        return;
      }

      if (filePath === DEMYTHOS_ASSISTANT_BOOT_PATH) {
        autoRunRef.current = true;
        addOutput('demythos', [{ text: 'Launching DeMythos...', type: 'info' }]);
        setInteractiveApp('demythos');
        return;
      }

      if (!navigator) return;

      await navigator.waitForInitialization();
      if (navigator.syncWithVFS) {
        await navigator.syncWithVFS();
      }

      if (filePath && /\.(bat|cmd)$/i.test(filePath)) {
        autoRunRef.current = true;
        addOutput('', [{ text: `Auto-running batch script: ${filePath}`, type: 'info' }]);
        await runScriptFromPath(filePath, 'bat');
      } else if (filePath && /\.sh$/i.test(filePath)) {
        autoRunRef.current = true;
        addOutput('', [{ text: `Auto-running shell script: ${filePath}`, type: 'info' }]);
        await runScriptFromPath(filePath, 'sh');
      }
    };

    void maybeAutoRun();
  }, [fileContent, filePath, navigator]);

  return (
    <>
      <div 
        className={cn(
          "h-full text-green-400 font-mono flex flex-col overflow-hidden",
          getTextColor()
        )}
        style={{
          backgroundColor: `rgba(0, 0, 0, ${terminalSettings.backgroundOpacity / 100})`,
          fontSize: `${terminalSettings.fontSize}px`,
          fontFamily: terminalSettings.fontFamily,
          color: getTextColorValue(),
          backdropFilter: terminalSettings.backgroundOpacity < 100 ? 'blur(1px)' : 'none'
        }}
        onClick={focusInput}
      >
      {activeSshSession && (
        <div className="flex items-center justify-between border-b border-emerald-500/20 bg-emerald-500/5 px-4 py-2 text-xs text-emerald-200">
          <span>
            Remote: {activeSshSession.username}@{activeSshSession.host}:{activeSshSession.port}
          </span>
          <span className="uppercase tracking-wide text-emerald-300/80">
            {activeSshSession.status}
          </span>
        </div>
      )}
      {deMythosTerminalStatus && (
        <div className="border-b border-cyan-500/20 bg-cyan-500/5 px-4 py-2 text-xs text-cyan-100">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold uppercase tracking-wide text-cyan-300/90">
              DeMythos · loop {deMythosSessionRun?.currentLoop || 0}/{deMythosSessionRun?.maxLoops || 0} · {deMythosSessionRun?.status || 'idle'}
            </span>
            <span className="text-cyan-300/70">
              {deMythosSessionRun?.updatedAt ? new Date(deMythosSessionRun.updatedAt).toLocaleTimeString() : ''}
            </span>
          </div>
          <div className="mt-1 whitespace-pre-wrap break-words text-cyan-100">
            {deMythosTerminalStatus.headline}
          </div>
          <div className="mt-1 whitespace-pre-wrap break-words text-cyan-300/80">
            Latest event: {deMythosTerminalStatus.detail}
          </div>
        </div>
      )}
      {shouldShowSshViewport ? (
        <div className="flex-1 min-h-0 p-4">
          <SshTerminalViewport
            sessionId={activeSshSession.sessionId}
            fontFamily={terminalSettings.fontFamily}
            fontSize={terminalSettings.fontSize}
            textColor={getTextColorValue()}
            onSendInput={sendInputToActiveSshSession}
            onResize={resizeActiveSshSession}
            onInterrupt={interruptActiveSshSession}
            onWriterChange={handleSshViewportWriterChange}
            onError={handleSshViewportError}
          />
        </div>
      ) : (
        <div
          ref={terminalRef}
          className="flex-1 overflow-y-auto p-4 space-y-1"
        >

          {interactiveApp === 'maplecode' ? (
            <MapleCodeREPL
              onExit={exitInteractiveApp}
              navigator={navigator}
              initialPath={currentPath}
              initialPrompt={mapleCodeInitialPrompt}
              onOpenWindow={onOpenWindow}
              availableTerminalCommands={BUILTIN_COMMANDS}
              availablePrograms={PROGRAMS.map(({ id, label, description }) => ({ id, label, description }))}
              runTerminalCommand={runTerminalCommandForMapleCode}
              submitTerminalInput={submitTerminalInputForMapleCode}
            />
          ) : interactiveApp === 'demythos' ? (
            <DeMythos
              onExit={exitInteractiveApp}
              initialPrompt={deMythosInitialPrompt}
              sshProfiles={sshProfiles}
              runTerminalCommand={runTerminalCommandForMapleCode}
              submitTerminalInput={submitTerminalInputForMapleCode}
            />
          ) : interactiveApp === 'ssh-config' ? (
            <SshConfigApp
              onExit={exitInteractiveApp}
              profiles={sshProfiles}
              onSaveProfile={handleSaveSshProfile}
              onDeleteProfile={handleDeleteSshProfile}
              isElectron={isElectronSshAvailable}
            />
          ) : interactiveApp === 'top' ? (

            <div
              ref={topViewRef}
              tabIndex={0}
              className="outline-none"
              style={{ whiteSpace: 'pre', fontFamily: terminalSettings.fontFamily, color: getTextColorValue() }}
            >
              {topLines.map((line, i) => (
                <div key={i} className="whitespace-pre">
                  {line}
                </div>
              ))}
            </div>
          ) : (
            <>
              {history.map((entry, index) => (
                <div key={index} className="space-y-1">
                  {entry.command && (
                    <div className="flex">
                      <span style={{ color: getTextColorValue() }}>{entry.prompt}</span>
                      <span style={{ color: getTextColorValue() }} className="ml-1">{entry.command}</span>
                    </div>
                  )}

                  {entry.output.map((line, lineIndex) => (
                    <div 
                      key={lineIndex} 
                      className={cn(
                        'whitespace-pre-wrap break-words',
                        getOutputColor(line.type)
                      )}
                    >
                      {line.text}
                    </div>
                  ))}
                </div>
              ))}
              
              {pendingTerminalInputPrompt && (
                <div className="mb-2 whitespace-pre-wrap break-words text-cyan-400">
                  {pendingTerminalInputPrompt}
                </div>
              )}

              {pendingSshPrompt && (
                <div className="mb-2 space-y-1 text-amber-200">
                  {pendingSshPrompt.description && (
                    <div className="whitespace-pre-wrap break-words text-xs text-amber-300/80">
                      {pendingSshPrompt.description}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words text-amber-200">
                    {pendingSshPrompt.prompt}
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="flex">
                <span style={{ color: getTextColorValue() }}>{promptText}</span>
                <input
                  ref={inputRef}
                  type={pendingSshPrompt?.secret ? 'password' : 'text'}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  disabled={isProcessing && !pendingSshPrompt && !pendingTerminalInputPrompt}
                  className="flex-1 bg-transparent ml-1 outline-none"
                  style={{ 
                    color: getTextColorValue(),
                    fontFamily: terminalSettings.fontFamily,
                  }}
                  placeholder={pendingSshPrompt
                    ? (pendingSshPrompt.secret ? 'Hidden input…' : 'Respond and press Enter…')
                    : pendingTerminalInputPrompt
                      ? 'Type input and press Enter…'
                      : ''}
                  autoFocus
                  spellCheck={false}
                />
              </form>
            </>
          )}
        </div>
      )}
    </div>

      {/* Settings Dialog */}
      {showSettings && (
        <TerminalSettingsDialog
          settings={terminalSettings}
          sshProfiles={sshProfiles}
          onChange={handleSettingsChange}
          onSaveSshProfile={handleSaveSshProfile}
          onDeleteSshProfile={handleDeleteSshProfile}
          onClose={() => setShowSettings(false)}
          isElectron={isElectronSshAvailable}
        />
      )}
    </>
  );
};